import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';

const PriceHistoryChart = ({ product, priceHistory, isVisible, onClose }) => {
  if (!isVisible) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatPrice = (value) => `$${value}`;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-floating">
          <p className="text-sm font-medium text-text-primary mb-1">
            {formatDate(label)}
          </p>
          <p className="text-lg font-bold text-primary">
            ${payload?.[0]?.value}
          </p>
        </div>
      );
    }
    return null;
  };

  const currentPrice = priceHistory?.[priceHistory?.length - 1]?.price || 0;
  const highestPrice = Math.max(...priceHistory?.map(p => p?.price));
  const lowestPrice = Math.min(...priceHistory?.map(p => p?.price));
  const averagePrice = priceHistory?.reduce((sum, p) => sum + p?.price, 0) / priceHistory?.length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-text-primary">Price History</h2>
            <p className="text-muted-foreground">{product?.title}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Price Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-muted rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Current Price</div>
              <div className="text-xl font-bold text-primary">${currentPrice}</div>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Highest Price</div>
              <div className="text-xl font-bold text-destructive">${highestPrice}</div>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Lowest Price</div>
              <div className="text-xl font-bold text-success">${lowestPrice}</div>
            </div>
            <div className="bg-muted rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Average Price</div>
              <div className="text-xl font-bold text-text-primary">${averagePrice?.toFixed(2)}</div>
            </div>
          </div>

          {/* Chart */}
          <div className="bg-muted rounded-lg p-4 mb-6">
            <h3 className="font-medium text-text-primary mb-4">90-Day Price Trend</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={priceHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={formatDate}
                    stroke="var(--color-muted-foreground)"
                  />
                  <YAxis 
                    tickFormatter={formatPrice}
                    stroke="var(--color-muted-foreground)"
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Line 
                    type="monotone" 
                    dataKey="price" 
                    stroke="var(--color-primary)" 
                    strokeWidth={2}
                    dot={{ fill: 'var(--color-primary)', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6, stroke: 'var(--color-primary)', strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Price Alerts */}
          <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Icon name="Bell" size={20} className="text-primary" />
              <div className="flex-1">
                <h4 className="font-medium text-text-primary">Set Price Alert</h4>
                <p className="text-sm text-muted-foreground">
                  Get notified when the price drops below ${lowestPrice}
                </p>
              </div>
              <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
                Set Alert
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PriceHistoryChart;